import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  Stack,
  SvgIcon,
  SxProps,
  Typography,
} from '@mui/material';
import { getColor } from '../../assets/theme';
import { Link, useNavigate } from 'react-router-dom';
import {
  // Info,
  // QuestionAnswer,
  School,
  Settings,
  ShoppingCartOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { Module } from '../../interfaces/module.interface';
import { useDispatch } from 'react-redux';
import useLayoutContext from '../../hooks/useLayoutContext';
import useToast from '../../hooks/useToast';
import { usePostUserModuleMutation } from '../../api/property.api.rtk';
import { useSnackbar } from 'notistack';
import { ReactNode, useEffect, useState } from 'react';
import { useInitialSetupContext } from '@pages/initial-setup';
import { useAppSelector } from '@store/index';
import { foInitialSetupUpdateStep } from '@store/slices/front-office/fo-initial-setup.slice';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import DialogRequestForTraining from '../dialog/dialog-request-for-training';
import DialogGuide from '../dialog/dialog-guide';
import { useGetDashboardDataWithinTokenQuery } from '@api/users-management/user-mangement.api.rtk';
import LoadingComponent from '../animation/loading-component';
import { appMode } from '@utils/url';
import { ModalWrapper } from '@hooks/useModal';
import PosISSelection from '@modules/point-of-sale/containers/initial-setup/pos-selection-i-s.container';
import { clearPOSStation } from '@modules/point-of-sale/slice/pos-stations.slice';

interface ModuleChipProps {
  color: string;
  icon: ReactNode;
  disabled?: boolean;
  label: string;
  onClick?: () => void;
  show?: boolean;
}

/**
 * ModuleChip Component
 *
 * This component renders the chips inside the card modules, displaying various details like info, Q&A, and initial setup.
 *
 */

const ModuleChip = ({
  color,
  icon,
  label,
  disabled,
  onClick,
  show,
}: ModuleChipProps) => {
  const { white } = getColor();

  const [hovered, setHovered] = useState(false);

  /**
   * Style object for buttons
   * @type {SxProps}
   */
  const buttonStyle: SxProps = {
    borderRadius: '100px',
    p: 0.5,
    height: 'fit-content',
    width: 'fit-content',
    maxWidth: 1000,
    bgcolor: hovered || show ? color : 'transparent',
    color: hovered || show ? white : color,
    display: 'flex',
    alignItems: 'center',
    transition: 'all ease .25s',
    cursor: onClick ? 'pointer' : 'default',
    pointerEvents: !!disabled ? 'none' : 'auto',
  };

  return (
    <Stack
      direction='row'
      sx={buttonStyle}
      onMouseEnter={show ? undefined : () => setHovered(true)}
      onMouseLeave={show ? undefined : () => setHovered(false)}
      onClick={onClick}
      gap={'6px'}
    >
      <Stack
        height='100%'
        alignItems='center'
        justifyContent='center'
        sx={{ opacity: !!disabled ? 0.5 : 1 }}
      >
        {icon}
      </Stack>
      <Collapse orientation='horizontal' in={hovered || show} unmountOnExit>
        <Typography
          noWrap
          sx={{
            pr: '3px',
            visibility: hovered || show ? 'visible' : 'hidden',
            fontSize: '12px!important',
          }}
        >
          {label}
        </Typography>
      </Collapse>
    </Stack>
  );
};

/**
 * ModuleCard Component
 *
 * This component renders a card for a module, displaying its icon, title, and action buttons.
 * It handles navigation to the module and user interactions based on the module's active status.
 *
 * @param {Object} props - The component props
 * @param {Module} props.module - The module object containing details like title, icon, and active status
 * @param {boolean} [props.guide] - Optional flag to indicate if this card is part of a guide
 * @returns {JSX.Element} The rendered ModuleCard component
 */
const ModuleCard = ({
  module,
  guide,
  disclosure,
}: {
  module: Module;
  guide?: boolean;
  disclosure?: UseDisclosureInterface;
}) => {
  const { white, primary, bgColor, secondary } = getColor();
  const { closeSnackbar } = useSnackbar();
  const { setInitialSetupModule } = useInitialSetupContext();
  const selectedPropertyValue = useAppSelector(
    (state) => state?.properties?.selectedProperty?.value,
  );

  const activeProperties = useAppSelector(
    (state) => state?.properties?.activeProperties,
  );

  const isActiveBackOffice = activeProperties?.find(
    (item) => item.link === '/back-office' && item.isActive,
  );

  const isActive = module.isActive;

  const { toast } = useToast();

  const { closeModal } = useLayoutContext();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [postUserModule] = usePostUserModuleMutation();

  const [moduleGuide, setModuleGuide] = useState({
    code: module.code,
    link: module.link,
  });

  /**
   * Handles navigation to the module
   * Posts the user module, closes modal, updates current module in store, and navigates to the module link
   */
  const handleNavigate = async ({
    moduleCode,
    moduleLink,
  }: {
    moduleCode?: string;
    moduleLink?: string;
  }) => {
    if (
      (isFOInitialSetup && module.code === 'FO') ||
      (isABKInitialSetup && module.code === 'ABK') ||
      (isPOSInitialSetup && (module.code === 'POS' || module.code === 'BO')) ||
      isUMOnboardingGuide
    ) {
      setModuleGuide({
        code: moduleCode || module.code,
        link: moduleLink || module.link,
      });

      return guideDisclosure.onOpen();
    }
    closeModal();
    navigate(moduleLink || module.link);
    setTimeout(() => {
      closeSnackbar();
    }, 100);
    await postUserModule(moduleCode || module.code!).unwrap();
  };

  /**
   * Displays a toast message when user doesn't have permission to access the module
   */
  const handleToast = () => {
    toast({
      severity: 'warning',
      message:
        'No Privilege: You do not have permission to proceed with this module.',
    });
  };

  useEffect(() => {
    dispatch(
      foInitialSetupUpdateStep({ key: 'hideInitialSetup', state: false }),
    );
  }, [selectedPropertyValue]);

  // DIALOG COMPONENT STATE
  const {
    isOpen: isOpenRequestTraining,
    onOpen: onOpenRequestTraining,
    onClose: onCloseRequestTraining,
  } = useDisclosure();

  const guideDisclosure = useDisclosure();
  const stationDisclosure = useDisclosure();

  // FO INITIAL SETUP
  const isFOInitialSetup =
    module?.showInitialSetup &&
    selectedPropertyValue !== 'All Properties' &&
    !module?.initialSetupDone &&
    module?.code === 'FO' &&
    module.isActive;

  const isABKInitialSetup =
    module?.showInitialSetup &&
    selectedPropertyValue !== 'All Properties' &&
    !module?.initialSetupDone &&
    module?.code === 'ABK' &&
    module.isActive;

  const isPOSInitialSetup =
    module?.showInitialSetup &&
    selectedPropertyValue !== 'All Properties' &&
    !module?.initialSetupDone &&
    module?.code === 'POS' &&
    module.isActive;

  // UM GUIDE
  const { data: umData = [], isLoading = false } =
    useGetDashboardDataWithinTokenQuery({
      refetchOnMountOrArgChange: true,
      skip: module.code !== 'UM',
    });

  const isHaveUMActiveUsers =
    umData?.data?.count?.usersActive > 1 && module.code === 'UM';

  const isUMOnboardingGuide = !isHaveUMActiveUsers && module?.code === 'UM';

  return (
    <>
      <Grid
        item
        {...{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 }}
        sx={{ position: 'relative' }}
      >
        {isLoading && module.code === 'UM' && (
          <LoadingComponent wrapperStyle={{ zIndex: 1 }} />
        )}
        <Card
          id={guide ? 'guide' : ''}
          sx={{
            bgcolor: white,
            borderRadius: 2,
            boxShadow: 0,
            p: 2,
            border: '1px solid #ECEFF1',
          }}
          onClick={!isActive ? handleToast : undefined}
        >
          <Stack gap={2} sx={{ opacity: !isActive ? 0.5 : 1 }}>
            <Stack direction='row' alignItems='center' gap={2}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: bgColor,
                  filter: !isActive ? 'grayscale(1)' : undefined,
                }}
                onClick={isActive ? () => handleNavigate({}) : undefined}
              >
                <SvgIcon
                  component={isActive ? module.icon : module.disabledIcon}
                  sx={{
                    fontSize: 64,
                    color: '#FFFFFF',
                    opacity: !isActive ? 0.375 : 1,
                  }}
                  shapeRendering='cripsEdges'
                />
              </Box>
              <Stack gap={1} flex={1}>
                <Typography
                  onClick={isActive ? () => handleNavigate({}) : undefined}
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {module.title}
                </Typography>

                <Stack
                  direction='row'
                  justifyContent='space-between'
                  sx={{
                    pointerEvents: !isActive ? 'none' : 'auto',
                  }}
                >
                  <Stack direction='row' gap={1}>
                    {/* <ModuleChip
                      color={primary}
                      icon={<QuestionAnswer />}
                      label='Q&A'
                      disabled
                    />
                    <ModuleChip
                      color={primary}
                      icon={<Info />}
                      label='Information'
                      disabled
                    /> */}
                    <ModuleChip
                      color={primary}
                      icon={<School />}
                      label='Request Training'
                      disabled={module.code !== 'FO'}
                      onClick={onOpenRequestTraining}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Stack
              direction='row'
              justifyContent={
                isFOInitialSetup || isABKInitialSetup || isPOSInitialSetup
                  ? 'space-between'
                  : 'flex-end'
              }
              alignItems={'center'}
              gap={1}
            >
              {(isFOInitialSetup || isABKInitialSetup || isPOSInitialSetup) && (
                <ModuleChip
                  color={secondary || '#FB9E3B'}
                  icon={<Settings />}
                  label='Initial Setup'
                  onClick={() => {
                    // if (appMode === 'production') {
                    //   return disclosure?.onOpen();
                    // }

                    if (module.code === 'POS') {
                      return stationDisclosure.onOpen();
                    }

                    setInitialSetupModule(module.code);
                  }}
                  disabled={!isActive}
                  show={true}
                />
              )}
              {module.code === 'POS' ? (
                <Stack
                  direction='row'
                  gap={1}
                  letterSpacing={{
                    md: undefined,
                    lg: 'inherit',
                  }}
                  whiteSpace='nowrap'
                  sx={{
                    flex: '0 1 auto',
                    minWidth: 0,
                  }}
                >
                  {module?.disableOnAllProperty &&
                    selectedPropertyValue !== 'All Properties' && (
                      <Button
                        variant='outlined'
                        disabled={!isActive}
                        onClick={() => {
                          dispatch(clearPOSStation());

                          handleNavigate({
                            moduleLink: '/point-of-sale/selection',
                          });
                        }}
                        sx={{ borderRadius: '8px' }}
                      >
                        POS
                      </Button>
                    )}
                  {isActiveBackOffice && (
                    <Button
                      variant='outlined'
                      disabled={!isActive}
                      onClick={async () =>
                        handleNavigate({
                          moduleCode: 'BO',
                          moduleLink: '/back-office/dashboard',
                        })
                      }
                      sx={{
                        borderRadius: '8px',
                        minWidth: '0px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      Back Office
                    </Button>
                  )}
                </Stack>
              ) : (
                <Button
                  variant='outlined'
                  disabled={!isActive}
                  onClick={() => handleNavigate({})}
                  sx={{ borderRadius: '8px', p: '6px 22px' }}
                >
                  Explore
                </Button>
              )}
            </Stack>
          </Stack>
        </Card>
      </Grid>

      {/* REQUEST FOR TRAINING */}
      {module.code === 'FO' && (
        <DialogRequestForTraining
          open={isOpenRequestTraining}
          onClose={onCloseRequestTraining}
        />
      )}

      {/* FO INITIAL SETUP GUIDE */}
      {isFOInitialSetup && module.code === 'FO' && (
        <DialogGuide
          {...{
            guideDisclosure,
            handleClick: () => {},
            icon: VerifiedUserOutlined,
            dontShowAgain: 'frontOffice',
            title: 'Front Office',
            welcomeMessage: 'Welcome to Xenia Cloud FOS!',
            highlightDesc: 'We’re excited to have you on board.',
            desc: (
              <Stack
                gap={0}
                sx={{
                  '.MuiTypography-root': {
                    fontFamily: 'Poppins',
                    fontSize: '16px',
                    fontWeight: 400,
                    letterSpacing: '.15px',
                  },
                  a: {
                    textDecoration: 'none',
                  },
                  ul: { listStyleType: 'none' },
                }}
              >
                <Stack gap={0.5}>
                  <Typography>
                    This guide will walk you through the essential steps to get
                    started quickly and efficiently.
                  </Typography>
                  <Typography>Setup Masterdata using Initial Setup.</Typography>
                </Stack>
                <ul>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1958'
                      target='_blank'
                    >
                      <Typography>Room Types</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1979'
                      target='_blank'
                    >
                      <Typography>Market Segment</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1978'
                      target='_blank'
                    >
                      <Typography>Business Source</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1961'
                      target='_blank'
                    >
                      <Typography>Rates</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1959'
                      target='_blank'
                    >
                      <Typography>Entries</Typography>
                    </Link>
                  </li>
                </ul>
              </Stack>
            ),
            customButton: (
              <>
                <Button
                  variant='contained'
                  onClick={() => {
                    guideDisclosure.onClose();
                    if (appMode === 'production') {
                      return disclosure?.onOpen();
                    }
                    setInitialSetupModule(module.code);
                  }}
                >
                  Go to Initial Setup
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    guideDisclosure.onClose();
                    setTimeout(() => {
                      postUserModule(module.code!);
                      closeModal();
                      navigate(module.link);
                      setTimeout(() => {
                        closeSnackbar();
                      }, 100);
                    }, 100);
                  }}
                >
                  Skip Tour
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    guideDisclosure.onClose();
                  }}
                >
                  Close
                </Button>
              </>
            ),
          }}
        />
      )}

      {/* ABK INITIAL SETUP GUIDE */}
      {isABKInitialSetup && module.code === 'ABK' && (
        <DialogGuide
          {...{
            guideDisclosure,
            handleClick: () => {},
            icon: VerifiedUserOutlined,
            dontShowAgain: 'bookkeeping',
            title: 'Accounting and Finance',
            welcomeMessage: 'Welcome to Hermes Cloud!',
            highlightDesc: 'We’re excited to have you on board.',
            desc: (
              <Stack
                gap={0}
                sx={{
                  '.MuiTypography-root': {
                    fontFamily: 'Poppins',
                    fontSize: '16px',
                    fontWeight: 400,
                    letterSpacing: '.15px',
                  },
                  a: {
                    textDecoration: 'none',
                  },
                  ul: { listStyleType: 'none' },
                }}
              >
                <Stack gap={0.5}>
                  <Typography>
                    This guide will walk you through the essential steps to get
                    started quickly and efficiently.
                  </Typography>
                  <Typography>Setup Masterdata using Initial Setup.</Typography>
                </Stack>

                <ul>
                  <li>
                    <Link
                      to='https://support.servoitsolutions.ph/kb/faq.php?id=2384'
                      target='_blank'
                    >
                      <Typography>Company Profile</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://support.servoitsolutions.ph/kb/faq.php?id=2328'
                      target='_blank'
                    >
                      <Typography>Accounting Period</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://support.servoitsolutions.ph/kb/faq.php?id=2339'
                      target='_blank'
                    >
                      <Typography>Account Mapping</Typography>
                    </Link>
                  </li>
                </ul>
              </Stack>
            ),
            customButton: (
              <>
                <Button
                  variant='contained'
                  onClick={() => {
                    guideDisclosure.onClose();
                    // if (appMode === 'production') {
                    //   return disclosure?.onOpen();
                    // }
                    setInitialSetupModule(module.code);
                  }}
                >
                  Go to Initial Setup
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    guideDisclosure.onClose();
                    setTimeout(() => {
                      postUserModule(module.code!);
                      closeModal();
                      navigate(module.link);
                      setTimeout(() => {
                        closeSnackbar();
                      }, 100);
                    }, 100);
                  }}
                >
                  Skip Tour
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    guideDisclosure.onClose();
                  }}
                >
                  Close
                </Button>
              </>
            ),
          }}
        />
      )}

      {/* POS INITIAL SETUP GUIDE */}
      {isPOSInitialSetup &&
        ((moduleGuide.code && ['POS', 'BO'].includes(moduleGuide.code)) ||
          module.code === 'POS') && (
          <DialogGuide
            {...{
              guideDisclosure,
              handleClick: () => {},
              icon: ShoppingCartOutlined,
              dontShowAgain: 'pointOfSale',
              title: 'IServe Point of Sale & Back Office',
              welcomeMessage: 'Welcome to iServe Cloud POS and Back Office!',
              highlightDesc: 'We’re excited to have you on board.',
              desc: (
                <Stack
                  gap={0}
                  mb={1}
                  sx={{
                    '.MuiTypography-root': {
                      fontFamily: 'Poppins',
                      fontSize: '16px',
                      fontWeight: 400,
                      letterSpacing: '.15px',
                    },
                    a: {
                      textDecoration: 'none',
                    },
                    ul: { listStyleType: 'none' },
                  }}
                >
                  <Stack gap={0.5}>
                    <Typography>
                      This guide will walk you through the essential steps to
                      get started quickly and efficiently.
                    </Typography>
                    <Alert severity='info' sx={{ color: '#00b8d9' }}>
                      Before you begin: Don’t forget to apply for the Permit to
                      Use from BIR. You can start configuring your system while
                      your application is in progress.
                    </Alert>
                  </Stack>

                  <Stack direction='row' gap={8} mt={2}>
                    <Stack>
                      <Typography sx={{ fontWeight: 'bold !important' }}>
                        Getting started
                      </Typography>
                      <ul>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2407'
                            target='_blank'
                          >
                            <Typography>BIR Permit to use</Typography>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2490'
                            target='_blank'
                          >
                            <Typography>Station details</Typography>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2491'
                            target='_blank'
                          >
                            <Typography>Printer setup</Typography>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2492'
                            target='_blank'
                          >
                            <Typography>Menu items</Typography>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2493'
                            target='_blank'
                          >
                            <Typography>Tables</Typography>
                          </Link>
                        </li>
                      </ul>
                    </Stack>

                    <Stack>
                      <Typography sx={{ fontWeight: 'bold !important' }}>
                        Other settings
                      </Typography>
                      <ul>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2494'
                            target='_blank'
                          >
                            <Typography>Service options</Typography>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2495'
                            target='_blank'
                          >
                            <Typography>Discount</Typography>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to='https://support.servoitsolutions.ph/kb/faq.php?id=2496'
                            target='_blank'
                          >
                            <Typography>Revenue Period</Typography>
                          </Link>
                        </li>
                      </ul>
                    </Stack>
                  </Stack>
                </Stack>
              ),
              customButton: (
                <>
                  <Button
                    variant='contained'
                    onClick={() => {
                      guideDisclosure.onClose();
                      // if (appMode === 'production') {
                      //   return disclosure?.onOpen();
                      // }
                      // setInitialSetupModule(moduleGuide.code);
                      stationDisclosure.onOpen();
                    }}
                  >
                    Go to Initial Setup
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      guideDisclosure.onClose();
                      setTimeout(() => {
                        postUserModule(moduleGuide.code!);
                        closeModal();
                        navigate(moduleGuide.link);
                        setTimeout(() => {
                          closeSnackbar();
                        }, 100);
                      }, 100);
                    }}
                  >
                    Skip Tour
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      guideDisclosure.onClose();
                    }}
                  >
                    Close
                  </Button>
                </>
              ),
            }}
          />
        )}
      {/* UM ONBOARDING GUIDE */}
      {isUMOnboardingGuide && (
        <DialogGuide
          {...{
            guideDisclosure,
            handleClick: () => {},
            icon: VerifiedUserOutlined,
            dontShowAgain: 'frontOffice',
            title: 'Users Management',
            welcomeMessage: 'Welcome to Xenia Cloud Users Management!',
            highlightDesc: 'We’re excited to have you on board.',
            desc: (
              <Stack
                gap={0}
                sx={{
                  '.MuiTypography-root': {
                    fontFamily: 'Poppins',
                    fontSize: '16px',
                    fontWeight: 400,
                    letterSpacing: '.15px',
                  },
                  a: {
                    textDecoration: 'none',
                  },
                  ul: { listStyleType: 'none' },
                }}
              >
                <Typography>
                  This guide will walk you through the essential steps to get
                  started managing users, roles and privileges.
                </Typography>

                <ul>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1941'
                      target='_blank'
                    >
                      <Typography>
                        Create Roles and Assign Privileges
                      </Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1944'
                      target='_blank'
                    >
                      <Typography>Create New User</Typography>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='https://servoitsolutions.ph/support/kb/faq.php?id=1980'
                      target='_blank'
                    >
                      <Typography>User Self-Enrollment</Typography>
                    </Link>
                  </li>
                </ul>
              </Stack>
            ),
            customButton: (
              <>
                <Button
                  variant='contained'
                  onClick={() => {
                    guideDisclosure.onClose();
                    setTimeout(() => {
                      postUserModule(moduleGuide.code ?? module.code!);
                      closeModal();
                      navigate(moduleGuide.link);
                      setTimeout(() => {
                        closeSnackbar();
                      }, 100);
                    }, 100);
                  }}
                >
                  Let's go
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    guideDisclosure.onClose();
                  }}
                >
                  Close
                </Button>
              </>
            ),
          }}
        />
      )}
      <ModalWrapper modalDisclosure={stationDisclosure}>
        <PosISSelection
          onClose={() => stationDisclosure.onClose()}
          moduleCode={'POS'}
          setInitialSetupModule={setInitialSetupModule}
        />
      </ModalWrapper>
    </>
  );
};

export default ModuleCard;
